import { api } from './api'
import { WaContact, WaTag, PaginatedResponse } from '@/types'

export interface WaContactFilters {
  accountId?: number
  search?: string
  tagIds?: number[]
  isBlocked?: boolean
  page?: number
  limit?: number
}

export interface CreateWaContactRequest {
  phone: string
  name?: string
  notes?: string
  tagIds?: number[]
}

export interface UpdateWaContactRequest {
  name?: string
  email?: string
  company?: string
  notes?: string
  isBlocked?: boolean
  tagIds?: number[]
  metadata?: Record<string, any>
}

export interface BulkWaContactRequest {
  contactIds: number[]
  action: 'delete' | 'block' | 'unblock' | 'addTags' | 'removeTags'
  tagIds?: number[]
}

export const waContactService = {
  async fetchContacts(filters: WaContactFilters = {}): Promise<PaginatedResponse<WaContact>> {
    const response = await api.get<{
      success: boolean
      data: WaContact[]
      pagination: {
        page: number
        limit: number
        total: number
        totalPages: number
      }
    }>('/whatsapp/contacts', { params: filters })
    return {
      data: response.data.data || [],
      pagination: response.data.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 },
    }
  },

  async getContact(id: number): Promise<WaContact> {
    const response = await api.get<{ success: boolean; data: WaContact }>(`/whatsapp/contacts/${id}`)
    return response.data.data
  },

  async createContact(data: CreateWaContactRequest): Promise<WaContact> {
    const response = await api.post<{ success: boolean; data: WaContact }>('/whatsapp/contacts', data)
    return response.data.data
  },

  async updateContact(id: number, data: UpdateWaContactRequest): Promise<WaContact> {
    const response = await api.put<{ success: boolean; data: WaContact }>(`/whatsapp/contacts/${id}`, data)
    return response.data.data
  },

  async deleteContact(id: number): Promise<void> {
    await api.delete(`/whatsapp/contacts/${id}`)
  },

  async bulkAction(data: BulkWaContactRequest): Promise<{ affected: number }> {
    const response = await api.post<{ success: boolean; data: { affected: number } }>('/whatsapp/contacts/bulk', data)
    return response.data.data
  },

  async blockContact(id: number): Promise<WaContact> {
    const response = await api.post<{ success: boolean; data: WaContact }>(`/whatsapp/contacts/${id}/block`)
    return response.data.data
  },

  async unblockContact(id: number): Promise<WaContact> {
    const response = await api.post<{ success: boolean; data: WaContact }>(`/whatsapp/contacts/${id}/unblock`)
    return response.data.data
  },

  // Tag operations
  async fetchTags(): Promise<WaTag[]> {
    const response = await api.get<{ success: boolean; data: WaTag[] }>('/whatsapp/tags')
    return response.data.data || []
  },

  async createTag(data: { name: string; color?: string; description?: string }): Promise<WaTag> {
    const response = await api.post<{ success: boolean; data: WaTag }>('/whatsapp/tags', data)
    return response.data.data
  },

  async updateTag(id: number, data: { name?: string; color?: string; description?: string }): Promise<WaTag> {
    const response = await api.put<{ success: boolean; data: WaTag }>(`/whatsapp/tags/${id}`, data)
    return response.data.data
  },

  async deleteTag(id: number): Promise<void> {
    await api.delete(`/whatsapp/tags/${id}`)
  },
}
